import './styles.scss';

import React, {
    FormEvent,
    useCallback,
    useContext,
    useRef,
    useState,
} from 'react';

import { ClientError } from '../../api';
import { Button } from '../../components/Button';
import { Container } from '../../components/Container';
import { Fields } from '../../components/Fields';
import { InputCheckbox } from '../../components/InputCheckbox';
import { Loader } from '../../components/Loader';
import { SimpleTitleSection } from '../../components/SimpleTitleSection';
import { useFormFields } from '../../hooks';
import {
    ApiContext,
    CoreContext,
} from '../../providers';
import { classNames } from '../../services/classNames';
import { parseHTML } from '../../services/parseHTML';
import { setError } from '../../state';
import {
    Storyblok,
    StoryblokSection,
} from '../../storyblok';

/**
 * TODO: Remove termsAgreemnt, termsText, termsCheckboxText, termsButtonLabel, stepTwoText
 * on Storyblok.Component.ContactForm?
 */

export const ContactForm = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.ContactForm>) => {
    const { Upscore } = useContext(ApiContext);
    const { store } = useContext(CoreContext);

    const {
        id,
        headline,
        fields,
        buttonLabel,
        successMessage,
        termsAgreement,
        errorMessage,
        termsText,
        termsCheckboxText,
        termsButtonLabel,
        stepTwoText,
        backgroundColor,
        color,
        buttonVariant,
        investorForm,
    } = sectionData;
    const consentCheckboxRef = useRef<HTMLInputElement>(null);
    const [fieldErrors, setFieldErrors, resetFieldErrors] =
        useFormFields(fields);
    const [fieldValues, setFieldValues, resetFieldValues] =
        useFormFields(fields);

    const [showTermsAgreement, setShowTermsAgreement] =
        useState(termsAgreement);
    const [agreementChecked, setAgreementChecked] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [formMessage, setFormMessage] = useState<any>(undefined);

    const handleFormSubmit = useCallback(
        async (event) => {
            event.preventDefault();
            setIsLoading(true);

            if (!Upscore.credentialsAvailable()) {
                setIsLoading(false);
                console.error(new Error('RequestID or userTokenID is missing'));

                return;
            }

            try {
                if (!investorForm) {
                    const { name, email, message, phoneNumber } = fieldValues;

                    const { result } = await Upscore.postContact({
                        name,
                        email,
                        message:
                            message || 'No message was provided by the user',
                        phoneNumber,
                    }).catch((error) => {
                        throw new ClientError(
                            'Upscore API contact request failed',
                            {
                                error,
                                category: 'Upscore API',
                                type: 'general',
                                extras: {
                                    error: JSON.stringify(error, null, 2),
                                },
                            },
                        );
                    });

                    if (result === true) {
                        setFormMessage(successMessage);
                        resetFieldValues();
                        resetFieldErrors();
                    } else {
                        setFormMessage(errorMessage);
                    }
                }
            } catch (error) {
                console.error(error);
                setFormMessage(errorMessage);

                if (
                    error instanceof ClientError &&
                    error.data.response &&
                    error.data.response.errors
                ) {
                    error.data.response.errors.forEach((e: any) => {
                        if (e.param === 'requestID') {
                            store.dispatch(setError('missing-requestid'));
                        } else {
                            setFieldErrors({
                                [e.param]: e.msg,
                            });
                        }
                    });
                } else {
                    store.dispatch(setError('general'));
                }
            }

            setIsLoading(false);
        },
        [
            errorMessage,
            fieldValues,
            resetFieldErrors,
            resetFieldValues,
            setFieldErrors,
            successMessage,
        ],
    );

    const onTermsAgreement = (event: FormEvent) => {
        event.preventDefault();

        setShowTermsAgreement(false);
    };

    const TermsAgreementForm = (
        <form
            className={classNames(
                'LoginForm',
                'BankLoginForm',
                `BankLoginForm--altinn`,
            )}
            onSubmit={onTermsAgreement}
        >
            {termsText && (
                <div className="termsText">
                    {parseHTML(termsText, 'markdown')}
                </div>
            )}

            <label className="FormField__label FormField__label--checkbox termsCheckbox">
                <input
                    type="checkbox"
                    name="consent"
                    id="consent"
                    required
                    className="FormField__input"
                    placeholder=""
                    data-tip="true"
                    data-for="errorMessage-consent"
                    data-event="profile form (field consent)"
                    value=""
                    onChange={() => setAgreementChecked(!agreementChecked)}
                    defaultChecked={false}
                    ref={consentCheckboxRef}
                />
                <InputCheckbox
                    checked={consentCheckboxRef.current?.checked || false}
                />
                {termsCheckboxText && (
                    <span>{parseHTML(termsCheckboxText, 'markdown')}</span>
                )}
            </label>

            <Button
                variant="boxed"
                className="BankLoginForm__button termsButton"
                data-event="altinn login form (terms)"
                formSubmit
            >
                {termsButtonLabel}
            </Button>
        </form>
    );

    const ContactForm = (
        <form
            action="POST"
            className={classNames(
                'ContactForm__form',
                isLoading ? 'ContactForm__form--loading' : '',
                investorForm ? 'InvestorForm' : '',
            )}
            onSubmit={handleFormSubmit}
        >
            {isLoading && <Loader backgroundColor="#54ef93" centered />}
            {stepTwoText && (
                <div className="stepTwoText">
                    {parseHTML(stepTwoText, 'markdown')}
                </div>
            )}
            <Fields
                fields={fields}
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                fieldErrors={fieldErrors}
                setFieldErrors={setFieldErrors}
                inputType="inputField"
            />
            {buttonLabel && (
                <Button
                    variant={buttonVariant}
                    formSubmit
                    data-event="contact form (submit button)"
                >
                    {buttonLabel}
                </Button>
            )}
        </form>
    );

    return (
        <section
            className="ContactForm"
            id={id}
            style={{ backgroundColor, color }}
        >
            <Container sizeGroup="size-3">
                {headline && (
                    <SimpleTitleSection
                        title={headline}
                        contentClassName="ContactForm__fields-wrapper"
                    >
                        {showTermsAgreement ? TermsAgreementForm : ContactForm}
                        {formMessage && (
                            <p className="ContactForm__message">
                                {formMessage}
                            </p>
                        )}
                    </SimpleTitleSection>
                )}
            </Container>
        </section>
    );
};
